import { InputLabel, TextField, InputAdornment, Popover } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { InputLabelProps } from '@mui/material/InputLabel'
import { CalenderIcon } from '@/src/assets/icons'
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { UseFormRegisterReturn } from 'react-hook-form'

interface FieldProps {
  variant?: 'filled' | 'outlined' | 'standard'
  label?: string
  fieldClass?: string
  divClass?: string
  disableAfter?: string | any
  disableBefore?: string | any
  defaultValue?: any
  placeholder?: string
  error?: boolean
  helperText?: any
  inputLabel?: string
  props?: object
  validation?: UseFormRegisterReturn<string>
  fullWidth?: boolean
  disabled?: boolean
  multiline?: boolean
  maxRows?: number
  rows?: number
  suffixIcon?: React.ReactNode
  prefixIcon?: React.ReactNode
  getvalue?: (data: any) => void
  value?: any
  InputLabelProps?: InputLabelProps
  customOnChange?: (date: any) => void
  disablePast?: boolean
}

const CalenderField = ({
  label = '',
  defaultValue,
  placeholder,
  helperText,
  error,
  validation,
  getvalue,
  disabled,
  value,
  variant = 'outlined',
  fieldClass = '',
  divClass = '',
  maxRows = 5,
  rows = 5,
  suffixIcon,
  prefixIcon,
  multiline = false,
  fullWidth = true,
  inputLabel = '',
  customOnChange,
  disablePast = false,
  disableAfter,
  disableBefore,
  ...props
}: FieldProps) => {
  const inputRef = useRef<HTMLDivElement | null>(null)
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
   const [date, setDate] = useState<Dayjs | null>(null)
  
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  
  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const handleCalender = (date: any) => {
    const dayjsDate = dayjs(date)
    setDate(dayjsDate)
    if (customOnChange) {
      customOnChange(dayjsDate)
    }
    setAnchorEl(null)
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus()
    }, 100)
  }

  useEffect(() => {
    if (!defaultValue) return

    const parsedDate = dayjs.isDayjs(defaultValue)
      ? defaultValue
      : dayjs(defaultValue)

    setDate(parsedDate)
  }, [defaultValue?.toString()]) // 👈 IMPORTANT

  const customDisableDates = (date: any) => {
    if (disableAfter) {
      return date.isAfter(disableAfter, 'day')
    } else if (disableBefore) {
      return date.isBefore(disableBefore, 'day')
    } else {
      return false // Changed from null to false
    }
  }

  return (
    <div className={`${divClass} relative`}>
      {inputLabel && <InputLabel className="font-semibold">{inputLabel}</InputLabel>}
      <TextField
        inputRef={inputRef}
        label={label}
        variant={variant}
        placeholder={placeholder}
        {...validation}
        helperText={helperText}
        error={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" className="border-none cursor-pointer">
              <button type="button" onClick={handleClick}>
                <CalenderIcon className="fill-primary-main h-6" />
              </button>
            </InputAdornment>
          ),
          className: `rounded-[0.625rem] border-icon-darkgray ${fieldClass}`
        }}
        value={dayjs(date).format('DD/MM/YYYY')}
        disabled={disabled}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        className="rounded-[1.25rem]"
        data-modal="true"
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            disablePast={disablePast}
            shouldDisableDate={customDisableDates}
            value={date}
            onChange={handleCalender}
            disabled={disabled}
          />
        </LocalizationProvider>
      </Popover>
    </div>
  )
}

export default CalenderField
